import { useLayoutEffect } from "react"

import createCache from "@emotion/cache"
import { CacheProvider } from "@emotion/react"
import { ThemeProvider } from "@mui/material"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import theme from "@skistarab/mui-theme"
import { QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { useRoute } from "wouter"

import ErrorBoundary from "@/components/ErrorBoundary"
import DataLayer from "@/features/DataLayer"
import MetaTags from "@/features/MetaTags"

import { useDateLocale, useSkipassFlow } from "./hooks"
import {
  SelectPassPage,
  StartPage,
  PassHoldersDetailsPage,
  EditPassHolderDetailsPage,
} from "./Pages"
import { createQueryClient } from "./queries/queryClient"
import "./i18n/config"

// Check if the user agent is Prerender if so we do not set the speedy option in the emotion cache. This allows prerender to evaluate the pages with CSS.
const shouldUseSpeedyOption = !navigator.userAgent
  .toLowerCase()
  .includes("prerender")
const emotionCache = createCache({
  key: "css-skipass",
  speedy: shouldUseSpeedyOption,
})
const queryClient = createQueryClient()

function SkipassFlow() {
  const {
    flowStep,
    searchView,
    actions: {
      selectCategory,
      returnToStartView,
      proceedToDetailsStep,
      returnToSearchStep,
    },
  } = useSkipassFlow()

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [flowStep, searchView])

  return (
    <>
      <DataLayer />
      <MetaTags />
      {flowStep === "search" && (
        <>
          {searchView === "start" && (
            <StartPage onCategorySelect={selectCategory} />
          )}
          {searchView === "selectPass" && (
            <SelectPassPage
              onBackNavigation={returnToStartView}
              onProceedToPassHoldersDetails={proceedToDetailsStep}
            />
          )}
        </>
      )}
      {flowStep === "details" && (
        <PassHoldersDetailsPage onBackNavigation={returnToSearchStep} />
      )}
    </>
  )
}

function App() {
  const [isEditRoute] = useRoute("/edit")
  const dateLocale = useDateLocale()

  return (
    <CacheProvider value={emotionCache}>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={dateLocale}
      >
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <ErrorBoundary>
              {isEditRoute ? <EditPassHolderDetailsPage /> : <SkipassFlow />}
            </ErrorBoundary>
            <ReactQueryDevtools />
          </QueryClientProvider>
        </ThemeProvider>
      </LocalizationProvider>
    </CacheProvider>
  )
}

export default App
